import { useId } from 'react';
import { Plus, SearchLoop } from 'assets/icons';
import {
  billingContactName,
  isBillingRecipient,
} from 'components/billingContacts/billingContactForm';
import { filterMembers } from 'components/billingContacts/billingContactSearch';
import { companyRoleLabel } from 'constants/companyRoles';
import UseContext from 'hooks/useContext';

/**
 * The search of mockup 8.3: the members of the company, filtered as the admin
 * types, then an option to add what was typed as a contact without an account.
 * Without any request: the members come in, the choices go out through
 * `onPickMember(member)` and `onAddExternal(query)`. `membersFallback`, when
 * given (members loading, or their read failed), takes the place of the list.
 */
const BillingContactPicker = ({
  members = [],
  membersFallback = null,
  query,
  onQueryChange,
  onPickMember,
  onAddExternal,
  isDisabled = false,
}) => {
  const { language } = UseContext();
  const text = (key) =>
    language.getValue(`companyBillingContacts.picker.${key}`, '');
  const searchId = useId();
  const groupId = useId();
  const typed = (query ?? '').trim();
  const shownMembers = filterMembers(members, typed);

  return (
    <div className={'flex flex-col gap-3 text-sm'}>
      <p className={'text-gray-500'}>{text('intro')}</p>
      <div className={'flex flex-col gap-1'}>
        <label htmlFor={searchId} className={'ft-sm text-gray-500'}>
          {text('searchLabel')}
        </label>
        <div className={'relative'}>
          <span
            className={
              'pointer-events-none absolute inset-y-0 left-3 flex items-center'
            }
          >
            <SearchLoop wh={18} color={'#6B7280'} />
          </span>
          <input
            id={searchId}
            type={'search'}
            value={query}
            placeholder={text('searchPlaceholder')}
            autoComplete={'off'}
            disabled={isDisabled}
            onChange={(event) => onQueryChange(event.target.value)}
            className={
              'w-full h-10 border rounded-md pl-10 pr-3 text-sm focus:outline-none focus:border-yellow-500'
            }
          />
        </div>
      </div>
      <div className={'flex flex-col gap-1'}>
        <div
          id={groupId}
          className={'ft-sm font-medium uppercase text-gray-500'}
        >
          {text('membersGroup')}
        </div>
        {/* Loading or failed: saying no member matches would be false. */}
        {membersFallback ? (
          membersFallback
        ) : shownMembers.length > 0 ? (
          <ul
            aria-labelledby={groupId}
            className={'flex flex-col max-h-64 overflow-y-auto'}
          >
            {shownMembers.map((member) => {
              const isRecipient = isBillingRecipient(member);
              const name = billingContactName(member) || member.email;
              const role = companyRoleLabel(language, member.role);
              return (
                <li key={member.idEmployee}>
                  {/* Already a recipient: shown so the admin knows, but a
                      disabled button, so it can be neither clicked nor
                      reached with the keyboard. */}
                  <button
                    type={'button'}
                    disabled={isDisabled || isRecipient}
                    onClick={() => onPickMember(member)}
                    data-testid={'billing-contact-picker-member'}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left ${
                      isRecipient
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={'flex flex-col min-w-0'}>
                      <span className={'truncate text-gray-900'}>
                        {[name, role].filter(Boolean).join(' · ')}
                      </span>
                      <span className={'truncate text-gray-500'}>
                        {member.email}
                      </span>
                    </span>
                    {isRecipient ? (
                      <span
                        className={
                          'flex-shrink-0 h-6 px-2 rounded-md bg-gray-100 text-gray-800 ft-sm flex items-center'
                        }
                      >
                        {text('alreadyRecipient')}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={'px-3 py-2 text-gray-500'}>{text('noMember')}</div>
        )}
      </div>
      {typed ? (
        <button
          type={'button'}
          disabled={isDisabled}
          onClick={() => onAddExternal(typed)}
          className={
            'flex items-start gap-3 px-3 py-2 border-t border-gray-100 text-left hover:bg-gray-50'
          }
        >
          <span className={'mt-0.5'}>
            <Plus wh={18} color={'#374151'} />
          </span>
          <span className={'flex flex-col min-w-0'}>
            {/* A function as the replacement: a "$&" typed in the search
                would otherwise be read as a pattern. */}
            <span className={'text-gray-900 break-words'}>
              {text('addExternal').replace('{query}', () => typed)}
            </span>
            <span className={'text-gray-500'}>{text('addExternalHint')}</span>
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default BillingContactPicker;
